import React, { useState } from "react";
import CalculatorPage from "./CalculatorPage";

function TipCalculator() {
  const [bill, setBill] = useState("");
  const [tipPercent, setTipPercent] = useState("");
  const [people, setPeople] = useState("");
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");
  const [funFact, setFunFact] = useState("");
  const [showFunFact, setShowFunFact] = useState(false);

  const onInput = (setter) => (e) => {
    setter(e.target.value);
    setResult(null);
    setShowFunFact(false);
    setError("");
  };

  const calculateTip = () => {
    const b = parseFloat(bill);
    const t = parseFloat(tipPercent);
    const n = parseInt(people, 10) || 1;
    if (isNaN(b) || isNaN(t) || b < 0 || t < 0 || n < 1) {
      setError("Please enter a bill amount and tip percentage.");
      setResult(null);
      setShowFunFact(false);
      return;
    }

    const tip = (b * t) / 100;
    const total = b + tip;

    setError("");
    setResult({
      tip: tip.toFixed(2),
      total: total.toFixed(2),
      perPerson: n > 1 ? (total / n).toFixed(2) : null,
      people: n,
    });

    const funFactsList = [
      "In Japan, tipping is considered rude.",
      "In the US, a 15-20% tip is standard at restaurants.",
      "Tipping customs vary widely across countries.",
      "Some restaurants include service charges automatically.",
      "In some cultures, tipping is seen as a sign of appreciation.",
    ];

    setFunFact(funFactsList[Math.floor(Math.random() * funFactsList.length)]);
    setShowFunFact(true);
  };

  return (
    <CalculatorPage
      title="Tip Calculator"
      metaDescription="Free tip calculator: work out the tip and total bill from a percentage, and split the total evenly between any number of people."
      intro={
        <>
          Calculate the <strong>tip amount</strong> and total bill — and
          optionally split it evenly between friends.
        </>
      }
      seoContent={
        <>
          <h2>How much should you tip?</h2>
          <p>
            In the United States, 15–20% is standard for table service, with
            18% a common middle ground. For counter service or takeout, 0–10%
            is typical. Customs differ widely by country — in Japan tipping
            can be seen as impolite, while much of Europe includes a service
            charge in the bill, making an extra tip optional.
          </p>
          <h2>Splitting the bill</h2>
          <p>
            Enter the number of people and the calculator divides the total
            (bill plus tip) evenly. A quick etiquette tip: calculate the tip
            on the pre-tax amount if you want to be precise, though most
            people simply tip on the total for simplicity.
          </p>
          <h2>Frequently asked questions</h2>
          <h3>Should I tip on the pre-tax or post-tax amount?</h3>
          <p>
            Etiquette guides suggest tipping on the pre-tax subtotal, since the
            tax isn't part of the service. In practice many people tip on the
            total because it's quicker; the difference is usually small.
          </p>
          <h3>What's a normal tip percentage?</h3>
          <p>
            For sit-down restaurant service in the US, 15–20% is standard.
            Exceptional service might warrant more, and for counter service or
            takeaway a smaller tip or none at all is common.
          </p>
          <h3>How do I split a bill with different tip amounts?</h3>
          <p>
            This calculator splits the total evenly. If people want to tip
            different amounts, calculate each person's share of the bill first,
            then let each add their own tip on top.
          </p>
        </>
      }
    >
      <div className="fields">
        <label className="field">
          <span>Bill Amount</span>
          <input type="number" value={bill} onChange={onInput(setBill)} placeholder="e.g. 84.50" />
        </label>
        <label className="field">
          <span>Tip (%)</span>
          <input type="number" value={tipPercent} onChange={onInput(setTipPercent)} placeholder="e.g. 18" />
        </label>
        <label className="field">
          <span>Split Between (optional)</span>
          <input type="number" value={people} onChange={onInput(setPeople)} placeholder="e.g. 4" min="1" />
        </label>
      </div>

      <button className="btn" onClick={calculateTip}>Calculate Tip</button>

      {error && <div className="error">{error}</div>}

      {result && (
        <>
          <div className="result result-breakdown">
            <div>
              Tip: <strong>{result.tip}</strong>
            </div>
            <div>
              Total with tip: <strong>{result.total}</strong>
            </div>
            {result.perPerson && (
              <div>
                Each of {result.people} people pays:{" "}
                <strong>{result.perPerson}</strong>
              </div>
            )}
          </div>
          {showFunFact && (
            <div className="fun-fact fun-fact-animate">💡 {funFact}</div>
          )}
        </>
      )}
    </CalculatorPage>
  );
}

export default TipCalculator;
